/**
 * Monthly status report editor.
 *
 * TrackIT's own UI language (slate cards, Danone Blue accents, the shared
 * Field/Select/UserPicker components) wrapping a live preview of the slide,
 * which follows the QBR deck style instead - see components/ReportSlide.
 *
 * Layout: a tabbed form on the left, the slide always visible on the right.
 * Tabs rather than one long scroll because the monthly job is "check what
 * needs filling, fill it" - each tab carries an amber dot while something in
 * it still needs attention, so the PM can see the whole to-do list without
 * scrolling past cards that are already done.
 *
 * Two things drive the design:
 *
 *  1. Nothing starts empty. Every RAG arrives pre-derived from the risk log,
 *     dates, costs and ledger (lib/report), with its reasoning shown. The PM
 *     checks four values and writes the narrative; they don't fill a form.
 *
 *  2. It edits the project too. Stage, milestones and budget live on the
 *     project document, not the report, but a PM doing their monthly update
 *     shouldn't have to bounce between two pages to fix a milestone date.
 *     Those fields write through to the project on save, and the report then
 *     freezes a snapshot of them.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft, Plus, Trash2, Maximize2, Check, Loader2, RotateCcw, Sparkles, AlertTriangle, CalendarDays, Download,
} from 'lucide-react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { useApi } from '../hooks/useApi'
import { useData } from '../hooks/useData'
import { useAuth } from '../hooks/useAuth'
import { closedMessage, fmtUk, resolveWindow, windowChipLabel } from '../lib/reportingWindow'
import LoadingSpinner from '../components/LoadingSpinner'
import Field, { inputClass } from '../components/Field'
import Select from '../components/Select'
import DateInput from '../components/DateInput'
import UserPicker from '../components/UserPicker'
import Modal from '../components/Modal'
import InfoTooltip from '../components/InfoTooltip'
import RichTextInput from '../components/RichTextInput'
import ReportSlide, { SLIDE_W, SLIDE_H, LIST_ROW_MIN_PX, RISKS_CARD_MIN_PX, type ListsSpaceInfo } from '../components/ReportSlide'
import { FullScreenSlide, useSlidePng } from '../components/SlideExport'
import {
  blankReservationPx, buildPrefill, buildSnapshot, currentReportMonth, deriveAll, effectiveRag,
  monthLabel, shiftMonth, suggestOverall,
} from '../lib/report'
import { createAutosaver, type Autosaver, type SaveState } from '../lib/autosave'
import { PROJECT_STAGES } from '../types'
import type {
  DatePrecision, Milestone, MilestoneStatus, Project, ProjectReport as Report, ProjectStage,
  RagAssessment, RagStatus, ReportBullet, Settings, UserRef, MonthReview} from '../types'

// Editor UI uses TrackIT's own status colours (Danone dark green #00843D, the
// same green as the rest of the app). The slide keeps the QBR palette.
const RAG_SOLID: Record<RagStatus, string> = {
  green: 'bg-[#00843D]', amber: 'bg-[#F88806]', red: 'bg-[#EF3340]',
}
const RAG_PILL: Record<RagStatus, string> = {
  green: 'bg-[#00843D]/10 text-[#00843D] border-[#00843D]/30',
  amber: 'bg-[#F88806]/10 text-[#B8640B] border-[#F88806]/30',
  red: 'bg-[#EF3340]/10 text-[#B8242E] border-[#EF3340]/30',
}
const RAG_LABEL: Record<RagStatus, string> = { green: 'Green', amber: 'Amber', red: 'Red' }
const RAGS: RagStatus[] = ['green', 'amber', 'red']

// A report's lifecycle, in order. The editor's buttons only move it forward.
const STATE_ORDER: Report['state'][] = ['draft', 'submitted', 'reviewed']

const MILESTONE_STATUSES: readonly (readonly [string, string])[] = [
  ['planned', 'Planned'], ['achieved', 'Achieved'], ['at_risk', 'At risk'], ['missed', 'Missed'],
]

type TabKey = 'status' | 'summary' | 'content' | 'milestones' | 'details'

// Compact variant of the shared input style - the editors here are dense
// row-per-item lists, and the standard py-2 field height reads oversized
// when a dozen of them stack up.
const smallInput = 'w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm'

const uid = (p: string) => `${p}_${Math.random().toString(36).slice(2, 9)}`

// How long editing has to pause before the report saves itself. Long enough
// that a sentence goes out as one save rather than one per word.
const AUTOSAVE_DELAY_MS = 1500

// Split view. Below SIDE_BY_SIDE the form and a readable preview cannot both
// fit, so the layout stacks and the slide takes the full content width.
// Above it the divider is draggable, because the right balance depends on the
// monitor and on whether you are writing or reviewing - the person doing it
// knows better than a hardcoded ratio.
const SIDE_BY_SIDE = '(min-width: 1400px)'
const SPLIT_KEY = 'trackit.reportSplitWidth'
const FORM_DEFAULT = 520
const FORM_MIN = 360
const PREVIEW_MIN = 420
const COL_GAP = 20   // matches gap-5

/** A section shell matching the rest of the app's detail pages. */
function Card({ title, children, action, info }: {
  title: string; children: React.ReactNode; action?: React.ReactNode; info?: React.ReactNode
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-slate-800 flex items-center gap-1.5">
          {title}
          {info && <InfoTooltip>{info}</InfoTooltip>}
        </h3>
        {action}
      </div>
      {children}
    </div>
  )
}

/** One dimension: what the data says, and the PM's chance to disagree on the
 *  record. The derived value is never hidden, even when overridden - that
 *  disagreement is the signal worth keeping. */
function DimensionRow({ label, value, onChange }: {
  label: string; value: RagAssessment; onChange: (a: RagAssessment) => void
}) {
  const eff = effectiveRag(value)
  const overridden = !!value.override
  return (
    <div className="py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3">
        <div className="w-20 shrink-0 text-[11px] font-semibold text-slate-500 uppercase">{label}</div>
        <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden text-[11px] font-semibold">
          {RAGS.map(r => (
            <button
              key={r}
              type="button"
              onClick={() => onChange({
                ...value,
                override: r === value.derived ? undefined : r,
                overrideReason: r === value.derived ? undefined : value.overrideReason,
              })}
              className={`px-2.5 py-1 transition-colors ${
                eff === r ? `${RAG_SOLID[r]} text-white` : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {RAG_LABEL[r]}
            </button>
          ))}
        </div>
        {overridden && (
          <button
            type="button"
            onClick={() => onChange({ ...value, override: undefined, overrideReason: undefined })}
            className="text-xs text-danone-cyan font-semibold hover:text-danone-blue flex items-center gap-1 ml-auto"
          >
            <RotateCcw className="w-3 h-3" /> Reset to derived
          </button>
        )}
      </div>
      {/* Always its own line so the four rows read identically regardless of
          how long each reason happens to be. Green carries no reason: a healthy
          dimension needs no explanation, only problems do. */}
      <div className="mt-1.5 pl-[5.75rem] flex items-center gap-1.5 text-xs text-slate-500 min-w-0">
        <span className={`w-2 h-2 rounded-full shrink-0 ${RAG_SOLID[value.derived]}`} />
        <span className="truncate">
          TrackIT says <b className="text-slate-600">{RAG_LABEL[value.derived]}</b>
          {value.derived !== 'green' && value.derivedReason ? ` · ${value.derivedReason}` : ''}
        </span>
      </div>
      {overridden && (
        <div className="mt-2 pl-[5.75rem]">
          <input
            value={value.overrideReason ?? ''}
            onChange={e => onChange({ ...value, overrideReason: e.target.value })}
            placeholder="Reason (optional)"
            className={smallInput}
          />
        </div>
      )}
    </div>
  )
}

/** Add / edit / remove bullets. Risk-sourced ones show their provenance and
 *  are not free-text editable - the risk register stays the source of truth. */
function BulletEditor({ items, onChange, placeholder, accent, addDisabled }: {
  items: ReportBullet[]
  onChange: (b: ReportBullet[]) => void
  placeholder: string
  accent: string
  /** Blocks adding when the slide's space budget is spent. Existing rows stay
   *  editable and removable - the way back is removing, not adding. */
  addDisabled?: boolean
}) {
  const set = (id: string, text: string) => onChange(items.map(b => (b.id === id ? { ...b, text } : b)))
  const remove = (id: string) => onChange(items.filter(b => b.id !== id))
  return (
    <div className="space-y-2">
      {items.map(b => (
        <div key={b.id} className="flex items-start gap-2">
          <span className={`w-2 h-2 rounded-full shrink-0 mt-2.5 ${accent}`} />
          <div className="flex-1 min-w-0">
            <RichTextInput
              value={b.text}
              onChange={v => set(b.id, v)}
              readOnly={!!b.riskId}
              // Risk-sourced bullets are owned by the register, so no toolbar.
              toolbar={b.riskId ? 'none' : 'focus'}
              className={`${smallInput} ${b.riskId ? 'bg-slate-50 text-slate-600' : ''}`}
            />
          </div>
          {b.riskId && (
            <span className="mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#005EB8]/10 text-[#005EB8] shrink-0"
                  title="Pulled from this project's risk log">
              RISK
            </span>
          )}
          <button type="button" onClick={() => remove(b.id)}
                  className="mt-1.5 text-slate-300 hover:text-[#EF3340] shrink-0" title="Remove">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      {addDisabled ? (
        <div className="flex items-center gap-1.5 text-xs text-[#B8640B] font-medium">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          No more space on the slide. Remove or shorten what you've used first.
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onChange([...items, { id: uid('b'), text: '' }])}
          className="text-xs text-danone-cyan font-semibold hover:text-danone-blue flex items-center gap-1"
        >
          <Plus className="w-3.5 h-3.5" /> {placeholder}
        </button>
      )}
    </div>
  )
}

function MilestoneEditor({ items, onChange }: {
  items: Milestone[]; onChange: (m: Milestone[]) => void
}) {
  const set = (id: string, patch: Partial<Milestone>) =>
    onChange(items.map(m => (m.id === id ? { ...m, ...patch } : m)))
  const sorted = [...items].sort((a, b) => (a.date || '').localeCompare(b.date || ''))
  return (
    <div className="space-y-2">
      {sorted.length === 0 && (
        <div className="text-sm text-slate-400 italic">No milestones yet. The timeline needs at least one.</div>
      )}
      {sorted.map(m => (
        <div key={m.id} className="flex items-center gap-2">
          <input
            value={m.label}
            onChange={e => set(m.id, { label: e.target.value })}
            placeholder="Milestone"
            className={`${smallInput} flex-1`}
          />
          <div className="w-40 shrink-0">
            <DateInput value={m.date} onChange={v => set(m.id, { date: v })} />
          </div>
          <div className="w-32 shrink-0">
            <Select
              value={m.status}
              onChange={v => set(m.id, { status: v as MilestoneStatus })}
              options={MILESTONE_STATUSES}
            />
          </div>
          <button type="button" onClick={() => onChange(items.filter(x => x.id !== m.id))}
                  className="text-slate-300 hover:text-[#EF3340] shrink-0" title="Remove">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...items, { id: uid('ms'), label: '', date: '', status: 'planned' }])}
        className="text-xs text-danone-cyan font-semibold hover:text-danone-blue flex items-center gap-1"
      >
        <Plus className="w-3.5 h-3.5" /> Add milestone
      </button>
    </div>
  )
}

/** Where autosave has got to. The wait for a pause and the save itself both
 *  read "Saving…": to the PM they are the same thing, not saved yet. */
function SaveIndicator({ state, savedAt, onRetry }: {
  state: SaveState; savedAt?: string; onRetry: () => void
}) {
  // A fixed minimum width stops the header shuffling each time the label
  // flips between Saving… and Saved.
  return (
    <span role="status" className="min-w-[76px] flex items-center justify-end gap-1.5 text-xs font-semibold text-slate-500">
      {state.status === 'error' ? (
        <>
          <AlertTriangle className="w-3.5 h-3.5 text-[#EF3340]" />
          <span className="text-[#B8242E]">Couldn't save</span>
          <span className="text-slate-300">·</span>
          <button type="button" onClick={onRetry} className="text-danone-cyan hover:text-danone-blue">Retry</button>
        </>
      ) : state.status === 'saved' ? (
        // A pre-filled report nobody has edited yet has nothing to confirm.
        savedAt && (
          <span className="flex items-center gap-1.5" title={`Last saved ${new Date(savedAt).toLocaleString()}`}>
            <Check className="w-3.5 h-3.5 text-[#00843D]" /> Saved
          </span>
        )
      ) : (
        <>
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
        </>
      )}
    </span>
  )
}

export default function ProjectReportPage() {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const month = params.get('month') || currentReportMonth()
  // Held here so the open tab survives switching month.
  const [tab, setTab] = useState<TabKey>('status')
  // The report on screen. It trails the URL while the current edits save.
  const [shown, setShown] = useState({ id: id!, month })
  // The mounted editor's autosaver, registered by the editor itself.
  const saverRef = useRef<Autosaver<Report['state']> | null>(null)
  // Where a failed save stopped the PM going: to the report the URL has
  // already moved to, or out of the page through the header link.
  const [blocked, setBlocked] = useState<{ kind: 'switch' } | { kind: 'leave'; to: string } | null>(null)

  // One editor per report, swapped only once the current one's edits are
  // saved. Autosave writes whatever its editor holds, so an editor must never
  // be handed another report - and the URL can change under it without the
  // month switcher, through browser Back and Forward. If the save fails, the
  // current report stays on screen with its edits and the PM is asked before
  // they are discarded.
  const wanted = `${id}_${month}`
  const current = `${shown.id}_${shown.month}`
  useEffect(() => {
    // Nothing to switch to. That includes Back or Forward returning to the
    // report on screen while the dialog is up, which must close it rather
    // than leave a discard offered with no switch behind it.
    if (wanted === current) {
      setBlocked(null)
      return
    }
    let cancelled = false
    setBlocked(null)
    void (saverRef.current?.flush() ?? Promise.resolve(true)).then(ok => {
      if (cancelled) return
      if (ok) setShown({ id: id!, month })
      else setBlocked({ kind: 'switch' })
    })
    return () => { cancelled = true }
  }, [wanted, current, id, month])

  // Leaving through the header link saves first too, so a failure is asked
  // about here rather than lost after the page has gone. Other navigation,
  // like the sidebar, can't be held without a data router; the editor still
  // saves on its way out.
  const leave = async (to: string) => {
    if (await (saverRef.current?.flush() ?? Promise.resolve(true))) navigate(to)
    else setBlocked({ kind: 'leave', to })
  }

  const leaving = blocked?.kind === 'leave'
  return (
    <>
      <ReportEditor key={current} id={shown.id} month={shown.month} tab={tab} setTab={setTab}
                    saverRef={saverRef} onLeave={leave} />
      <Modal
        open={blocked !== null}
        title="Discard unsaved changes?"
        message={`Your latest edits to the ${monthLabel(shown.month)} report could not be saved. ${
          leaving ? 'Leaving' : 'Switching months'} will discard them.`}
        variant="danger"
        confirmLabel={leaving ? 'Discard and leave' : 'Discard and switch'}
        cancelLabel="Stay here"
        onConfirm={() => {
          saverRef.current?.discard()
          setBlocked(null)
          if (blocked?.kind === 'leave') navigate(blocked.to)
          else setShown({ id: id!, month })
        }}
        onCancel={() => {
          setBlocked(null)
          // A failed switch has already moved the URL; put it back.
          if (!leaving) navigate(`/projects/${shown.id}/report?month=${shown.month}`, { replace: true })
        }}
      />
    </>
  )
}

function ReportEditor({ id, month, tab, setTab, saverRef, onLeave }: {
  id: string
  month: string
  tab: TabKey
  setTab: (tab: TabKey) => void
  saverRef: React.MutableRefObject<Autosaver<Report['state']> | null>
  /** Leaves the page for `to`, saving first. */
  onLeave: (to: string) => void
}) {
  const [, setParams] = useSearchParams()
  const api = useApi()
  useDocumentTitle('Status Report')

  const { data, loading } = useData(
    () =>
      Promise.all([
        api.getProject(id),
        api.getSettings(),
        api.getProjectReport(month, id),
        api.getProjectReport(shiftMonth(month, -1), id),
        api.getMonthReview(month),
      ]).then(([project, settings, report, previous, review]) => ({ project, settings, report, previous, review: review as MonthReview })),
    [id, month],
  )

  // The reporting window decides whether Submit / Mark reviewed are offered.
  // Drafts can always be saved; admins are exempt so stragglers can be handled.
  const { user } = useAuth()
  const isAdmin = !!user?.isAdmin
  const windowState = useMemo(() => resolveWindow(month, data?.review, id), [month, data, id])
  const canSubmit = isAdmin || windowState.isOpen

  const [report, setReport] = useState<Report | null>(null)
  // Project-owned fields the editor writes through on save.
  const [stage, setStage] = useState<ProjectStage | undefined>()
  const [milestones, setMilestones] = useState<Milestone[]>([])
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [vp, setVp] = useState<UserRef | undefined>()
  const [lead, setLead] = useState<UserRef | undefined>()
  const [pm, setPm] = useState<UserRef | undefined>()
  const [capex, setCapex] = useState('')
  const [opex, setOpex] = useState('')
  const [capexCommitted, setCapexCommitted] = useState('')
  const [carriedFrom, setCarriedFrom] = useState<string | null>(null)
  const [saveState, setSaveState] = useState<SaveState>({ status: 'saved' })
  const [submitting, setSubmitting] = useState(false)
  const [fullSize, setFullSize] = useState(false)
  // The preview at native size is what the PNG is taken from, so the file is
  // the slide itself rather than the scaled-down copy on the page.
  const { ref: slideRef, download, exporting, error: exportError } = useSlidePng()
  const [summaryClipped, setSummaryClipped] = useState(false)
  // What the slide's lists column reported after fitting itself - drives the
  // out-of-space warnings and the risks toggle guard.
  const [listsSpace, setListsSpace] = useState<ListsSpaceInfo | null>(null)

  // No Save button: edits save themselves (lib/autosave). `persist` is rebuilt
  // each render around the current edits and reached through this ref, so a
  // save sends what is on screen when it runs, not when it was scheduled.
  const persistRef = useRef<(nextState?: Report['state']) => Promise<void>>(() => Promise.resolve())
  const [saver] = useState(() => createAutosaver<Report['state']>({
    delayMs: AUTOSAVE_DELAY_MS,
    save: nextState => persistRef.current(nextState),
    onState: setSaveState,
  }))
  // What this editor last wrote to the project. The first save of a visit
  // always writes (it flags the project reportable and counts as an update
  // for the staleness signal); after that only a change to a project field
  // does, so typing the narrative doesn't rewrite the project every pause.
  const lastProjectWrite = useRef<string | null>(null)

  useEffect(() => {
    // The page saves through this before it switches to another report.
    saverRef.current = saver
    // Closing or reloading the tab with a save outstanding gets the browser's
    // "Leave site?" prompt, and starts the save in case the PM stays.
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!saver.busy()) return
      void saver.flush()
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      if (saverRef.current === saver) saverRef.current = null
      // Month switches and the header link only unmount once the edits are
      // saved or discarded. Anything else leaving the page (the sidebar)
      // still saves what's pending here, on a best-effort basis.
      void saver.flush()
    }
  }, [saver, saverRef])
  // The slide is a fixed 1280px canvas scaled to whatever width its column
  // has, so it fills a wide monitor and still shrinks on a laptop.
  //
  // Attached via a callback ref, not an effect: this component returns a
  // spinner while loading, so on mount the ref is still null and an effect
  // would silently never observe anything. The callback fires when the node
  // actually appears. Zero widths are ignored - a transient 0 during layout
  // would otherwise collapse the slide to nothing.
  const [previewW, setPreviewW] = useState(SLIDE_W * 0.5625)
  const observerRef = useRef<ResizeObserver | null>(null)

  // Side-by-side is a media query rather than a CSS class alone, because the
  // dragged width must only be applied in that mode - a fixed width on a
  // stacked form would leave it stranded at 520px on a narrow screen.
  const [isWide, setIsWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(SIDE_BY_SIDE).matches,
  )
  useEffect(() => {
    const mq = window.matchMedia(SIDE_BY_SIDE)
    const onChange = (e: MediaQueryListEvent) => setIsWide(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const [formW, setFormW] = useState(() => {
    const saved = Number(localStorage.getItem(SPLIT_KEY))
    return Number.isFinite(saved) && saved >= FORM_MIN ? saved : FORM_DEFAULT
  })
  const rowRef = useRef<HTMLDivElement>(null)
  // Row width is captured once per drag rather than read on every move: it is
  // constant for the gesture, and recomputing it from the live columns would
  // feed the resize back into itself.
  // `latest` carries the applied width so the drag persists exactly what the
  // user let go on; reading state in the pointerup handler can lag the final
  // pointermove by a render.
  const dragRef = useRef<{ startX: number; startW: number; rowW: number; latest: number } | null>(null)

  const clampForm = (w: number, rowW: number) =>
    Math.round(Math.min(Math.max(FORM_MIN, w), Math.max(FORM_MIN, rowW - COL_GAP - PREVIEW_MIN)))

  const startDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = {
      startX: e.clientX,
      startW: formW,
      rowW: rowRef.current?.getBoundingClientRect().width ?? formW + COL_GAP + previewW,
      latest: formW,
    }
  }
  const onDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current
    if (!d) return
    d.latest = clampForm(d.startW + (e.clientX - d.startX), d.rowW)
    setFormW(d.latest)
  }
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current
    if (!d) return
    dragRef.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
    localStorage.setItem(SPLIT_KEY, String(d.latest))
  }
  const nudge = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowLeft' ? -32 : e.key === 'ArrowRight' ? 32 : 0
    if (!step) return
    e.preventDefault()
    const rowW = rowRef.current?.getBoundingClientRect().width ?? formW + COL_GAP + previewW
    const next = clampForm(formW + step, rowW)
    setFormW(next)
    localStorage.setItem(SPLIT_KEY, String(next))
  }
  const resetSplit = () => {
    setFormW(FORM_DEFAULT)
    localStorage.setItem(SPLIT_KEY, String(FORM_DEFAULT))
  }

  const previewRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    if (!node) return
    const measure = (w: number) => { if (w > 0) setPreviewW(w) }
    measure(node.getBoundingClientRect().width)
    const ro = new ResizeObserver(([entry]) => measure(entry.contentRect.width))
    ro.observe(node)
    observerRef.current = ro
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  const typed = data as { project: Project | null; settings: Settings; report: Report | null; previous: Report | null } | undefined
  const project = typed?.project
  const settings = typed?.settings
  const budgetYear = month.slice(0, 4)

  // Seed local state whenever the project/month changes. An existing report is
  // loaded as-is but its RAGs are re-derived, so a draft left open across a
  // risk-log change never shows a stale position.
  useEffect(() => {
    if (!project || !settings) return
    const existing = typed?.report
    if (existing) {
      setReport({ ...existing, ...deriveAll(project, settings, month, existing) })
      setCarriedFrom(null)
    } else {
      const { draft, carriedFromMonth } = buildPrefill(project, settings, month, typed?.previous)
      setReport(draft)
      setCarriedFrom(carriedFromMonth)
    }
    setStage(project.stage)
    setMilestones(project.milestones ?? [])
    setStartDate(project.startDate ?? '')
    setEndDate(project.endDate ?? '')
    setVp(project.functionalVP)
    setLead(project.functionalLead)
    setPm(project.projectManager ?? project.createdBy)
    const b = project.budgetByYear?.[budgetYear]
    setCapex(b?.capex != null ? String(b.capex) : '')
    setOpex(b?.opex != null ? String(b.opex) : '')
    setCapexCommitted(b?.capexCommitted != null ? String(b.capexCommitted) : '')
    // Intentionally keyed on the fetched payload + month only: re-running on
    // every local edit would clobber the PM's in-progress typing.
  }, [typed?.project, typed?.settings, typed?.report, typed?.previous, month, budgetYear, project, settings])

  const patch = (p: Partial<Report>) => {
    setReport(r => (r ? { ...r, ...p } : r))
    saver.edited()
  }

  /** The project as it would be after saving - drives the live preview so the
   *  slide reflects unsaved milestone and budget edits immediately. */
  const previewProject = useMemo<Project | null>(() => {
    if (!project) return null
    return {
      ...project,
      stage, milestones, startDate: startDate || undefined, endDate: endDate || undefined,
      functionalVP: vp, functionalLead: lead, projectManager: pm,
      budgetByYear: {
        ...(project.budgetByYear ?? {}),
        [budgetYear]: {
          capex: Number(capex) || 0,
          opex: Number(opex) || 0,
          capexCommitted: capexCommitted === '' ? undefined : Number(capexCommitted),
          // Not edited here yet; keep whatever the project already holds so a
          // save can't silently wipe it.
          opexCommitted: project.budgetByYear?.[budgetYear]?.opexCommitted,
        },
      },
    }
  }, [project, stage, milestones, startDate, endDate, vp, lead, pm, capex, opex, capexCommitted, budgetYear])

  const previewReport = useMemo<Report | null>(
    () => (report && previewProject
      ? { ...report, snapshot: buildSnapshot(previewProject, month, { riskLabels: report.riskLabels }) }
      : null),
    [report, previewProject, month],
  )

  if (loading || !typed) return <LoadingSpinner />
  if (!project) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Project not found.</p>
        <Link to="/projects" className="text-danone-cyan font-semibold text-sm">Back to Projects</Link>
      </div>
    )
  }
  if (!report || !previewReport || !settings) return <LoadingSpinner />

  const suggested = suggestOverall(report)

  // Per-tab "still needs attention" flags - these drive the amber dots on the
  // tab bar, which is how the PM sees what's left without scrolling anything.
  const realMilestones = milestones.filter(m => m.label.trim() || m.date)
  // Human-readable list of what the slide had to leave off, e.g.
  // "2 highlights, 1 lowlight and the asks". Empty when everything fits.
  const hiddenParts: string[] = []
  if (listsSpace) {
    const part = (n: number, one: string, many: string) =>
      n > 0 && hiddenParts.push(`${n} ${n === 1 ? one : many}`)
    part(listsSpace.hidden.highlights, 'highlight', 'highlights')
    part(listsSpace.hidden.lowlights, 'lowlight', 'lowlights')
    part(listsSpace.hidden.risks, 'risk', 'risks')
    part(listsSpace.hidden.supportNeeded, 'ask', 'asks')
  }
  const hiddenSummary = hiddenParts.length > 1
    ? `${hiddenParts.slice(0, -1).join(', ')} and ${hiddenParts[hiddenParts.length - 1]}`
    : hiddenParts[0] ?? ''

  // Entry-time guards: filling stops when the budget is spent, rather than
  // quietly knocking lower sections off the slide. One more row in an existing
  // card costs LIST_ROW_MIN_PX; the first ask also has to pay for its card.
  // While anything is already hidden the budget is beyond spent, so adds stay
  // blocked even though freePx reads as zero-ish rounding.
  //
  // Blank rows spend the budget too. They never render, so the slide's
  // measured free space cannot see them - and without the reservation,
  // stacking empty rows and populating them afterwards walked straight past
  // this guard and let the fallback trim knock lower sections off.
  const blanksOf = (items: ReportBullet[]) => items.filter(b => !b.text.trim()).length
  const asksHaveReal = report.supportNeeded.some(b => b.text.trim())
  const reservedPx = blankReservationPx(
    {
      highlights: blanksOf(report.highlights),
      lowlights: blanksOf(report.lowlights),
      supportNeeded: blanksOf(report.supportNeeded),
    },
    asksHaveReal,
    LIST_ROW_MIN_PX,
    RISKS_CARD_MIN_PX,
  )
  const effectiveFreePx = listsSpace == null ? null : Math.max(0, listsSpace.freePx - reservedPx)

  const overBudget = (listsSpace?.hiddenTotal ?? 0) > 0
  const noRoomForRow = overBudget || (effectiveFreePx != null && effectiveFreePx < LIST_ROW_MIN_PX)
  // A blank ask already reserved the card, so the next one costs only a row.
  const asksNeedCard = report.supportNeeded.length === 0
  const noRoomForAsk = overBudget || (effectiveFreePx != null &&
    effectiveFreePx < (asksNeedCard ? RISKS_CARD_MIN_PX : LIST_ROW_MIN_PX))

  // The risks card can only come back if the space it needs still exists,
  // after reservations. Only guards enabling: hiding is always allowed.
  const noRoomForRisks = !!report.hideRisks && effectiveFreePx != null && effectiveFreePx < RISKS_CARD_MIN_PX

  const needs: Record<TabKey, boolean> = {
    status: false,
    summary: !report.whyLine.trim() || !report.narrative.trim(),
    content: (listsSpace?.hiddenTotal ?? 0) > 0,
    milestones: realMilestones.length === 0,
    details: !stage || !vp || !pm,
  }

  const TABS: { key: TabKey; label: string }[] = [
    { key: 'status', label: 'Status' },
    { key: 'summary', label: 'Summary' },
    { key: 'content', label: 'Highlights & Asks' },
    { key: 'milestones', label: 'Milestones' },
    { key: 'details', label: 'Details' },
  ]

  // The page saves before it switches, and asks before discarding if that
  // save fails - the same path browser Back and Forward take.
  const changeMonth = (delta: number) => setParams({ month: shiftMonth(month, delta) })

  const cleanBullets = (items: ReportBullet[]) => items.filter(b => b.text.trim() !== '')

  // Writes the report as it stands. Autosave calls this through persistRef;
  // Submit / Mark reviewed passes the state to move to.
  const persist = async (nextState?: Report['state']) => {
    // A submitted or reviewed report only changes inside the month's window,
    // and Submit / Mark reviewed need it open too (the backend enforces both;
    // admins are exempt). Refused before any network call, so the project
    // fields are not written and then the report turned away.
    if ((nextState || report.state !== 'draft') && !canSubmit) {
      throw new Error(closedMessage(month, windowState))
    }
    // Rows the PM added but never filled in are dropped, not persisted.
    const keptMilestones = milestones.filter(m => m.label.trim() || m.date)
    // Project-owned fields first, so the snapshot we freeze matches what the
    // project will actually hold after this save.
    const projectFields = {
      stage: stage ?? null,
      milestones: keptMilestones,
      startDate: startDate || null,
      endDate: endDate || null,
      functionalVP: vp ?? null,
      functionalLead: lead ?? null,
      projectManager: pm ?? null,
      reportable: true,
      budgetByYear: previewProject!.budgetByYear,
    }
    const projectKey = JSON.stringify(projectFields)
    if (projectKey !== lastProjectWrite.current) {
      await api.updateProject(project.id, projectFields)
      lastProjectWrite.current = projectKey
    }
    const body: Report = {
      ...report,
      highlights: cleanBullets(report.highlights),
      lowlights: cleanBullets(report.lowlights),
      supportNeeded: cleanBullets(report.supportNeeded),
      snapshot: buildSnapshot({ ...previewProject!, milestones: keptMilestones }, month, { riskLabels: report.riskLabels }),
    }
    // A content save keeps the state the server holds, so a sign-off made
    // elsewhere while this page was open survives it. Submit and Mark reviewed
    // then move state on their own, and only ever forward: clicked on a page
    // that is behind, they must not pull back a report that is already past it.
    // The state just saved goes along as the expected one, so a sign-off that
    // lands between these two requests stops the move on the server too.
    let saved = await api.saveProjectReport(month, project.id, body) as Report
    if (nextState && STATE_ORDER.indexOf(nextState) > STATE_ORDER.indexOf(saved.state)) {
      saved = await api.setProjectReportState(month, project.id, nextState, saved.state) as Report
    }
    // Take only what the server stamps, including the state it now holds.
    // Swapping in the saved report whole would undo anything typed while this
    // save was in flight, and remove blank rows the PM has only just added.
    const { state, submittedBy, submittedAt, reviewedBy, reviewedAt, updatedBy, updatedAt, submittedAfterDue } = saved
    setReport(r => (r ? { ...r, state, submittedBy, submittedAt, reviewedBy, reviewedAt, updatedBy, updatedAt, submittedAfterDue } : r))
  }
  persistRef.current = persist

  const submit = async () => {
    setSubmitting(true)
    // Queued behind any save in flight, and carries every pending edit with it.
    if (await saver.saveNow(report.state === 'draft' ? 'submitted' : 'reviewed')) setCarriedFrom(null)
    setSubmitting(false)
  }

  // Fill whatever the preview column offers, up to the slide's native 1280px.
  // Capped at 1 because the slide is designed at that size; scaling past it
  // would just blur text without showing anything more.
  const scale = Math.min(1, previewW / SLIDE_W)

  return (
    <div className="h-full overflow-y-auto pb-10">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="min-w-0">
          <button onClick={() => onLeave(`/projects/${project.id}`)}
                  className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1.5 mb-1">
            <ArrowLeft className="w-4 h-4" /> {project.name}
          </button>
          <h1 className="text-2xl font-bold text-slate-800">Status Report</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden">
            <button onClick={() => changeMonth(-1)}
                    className="px-2.5 py-2 text-slate-500 hover:bg-slate-50 text-sm">‹</button>
            <div className="px-3 py-2 text-sm font-semibold text-slate-700 min-w-[130px] text-center">
              {monthLabel(month)}
            </div>
            <button onClick={() => changeMonth(1)}
                    className="px-2.5 py-2 text-slate-500 hover:bg-slate-50 text-sm">›</button>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${
            report.state === 'reviewed' ? 'bg-[#00843D]/10 text-[#00843D]'
              : report.state === 'submitted' ? 'bg-[#005EB8]/10 text-[#005EB8]'
                : 'bg-slate-100 text-slate-500'}`}>
            {report.state}
          </span>
          <span
            title={windowState.source === 'extension' ? 'This project has an extension for the month'
              : windowState.source === 'default' ? 'Default rule: open through the 7th of the following month'
                : windowState.dueAt ? `Due ${fmtUk(windowState.dueAt)} · locks ${fmtUk(windowState.closesAt)}`
                  : 'Reporting window for the month'}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
              windowState.pastDue ? 'bg-[#F88806]/10 text-[#B8640B] border-[#F88806]/30'
                : windowState.isOpen ? 'bg-[#00ACED]/10 text-[#0077A8] border-[#00ACED]/30'
                  : windowState.notYetOpen || windowState.dueAt ? 'bg-slate-100 text-slate-500 border-slate-200'
                    : 'bg-[#F88806]/10 text-[#B8640B] border-[#F88806]/30'}`}>
            <CalendarDays className="w-3.5 h-3.5" />
            {windowChipLabel(windowState)}
          </span>
          <SaveIndicator state={saveState} savedAt={report.updatedAt} onRetry={() => saver.retry()} />
          <button onClick={submit} disabled={submitting || !canSubmit}
                  title={canSubmit ? undefined : closedMessage(month, windowState)}
                  className="bg-[#005EB8] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#004a94] flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
            <Check className="w-4 h-4" /> {report.state === 'draft' ? 'Submit' : 'Mark reviewed'}
          </button>
        </div>
      </div>

      {carriedFrom && (
        <div className="mb-4 rounded-xl border border-[#00ACED]/30 bg-[#00ACED]/5 px-4 py-3 flex items-start gap-2.5">
          <Sparkles className="w-4 h-4 text-[#0077A8] shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700">
            <b>Pre-filled from {monthLabel(carriedFrom)}.</b> Narrative and asks carried forward;
            RAGs re-derived from the current risk log. Review the narrative before submitting.
          </div>
        </div>
      )}

      {saveState.status === 'error' && (
        <div className="mb-4 rounded-xl border border-[#EF3340]/30 bg-[#EF3340]/5 px-4 py-3 text-sm text-[#B8242E]">
          Your latest changes haven't been saved. {saveState.error}
        </div>
      )}

      {!windowState.isOpen && (
        <div className={`mb-4 rounded-xl border px-4 py-3 flex items-start gap-2.5 text-sm ${
          isAdmin ? 'border-slate-200 bg-slate-50 text-slate-600' : 'border-[#F88806]/30 bg-[#F88806]/5 text-[#7A4A00]'}`}>
          <CalendarDays className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            {closedMessage(month, windowState)}
            {isAdmin && ' As a platform admin you can still submit and validate.'}
            {isAdmin && windowState.closed && windowState.dueAt && report.state === 'draft' && ' A submission now is marked after cut-off.'}
            {!isAdmin && (report.state === 'draft'
              ? ' You can still save a draft.'
              : ' This report has already been submitted, so further changes need an extension.')}
          </div>
        </div>
      )}

      {windowState.pastDue && report.state === 'draft' && windowState.dueAt && (
        <div className="mb-4 rounded-xl border border-[#F88806]/30 bg-[#F88806]/5 px-4 py-3 flex items-start gap-2.5 text-sm text-[#7A4A00]">
          <CalendarDays className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            The cut-off was {fmtUk(windowState.dueAt)}. You can still submit until {fmtUk(windowState.closesAt)},
            and this report will be marked after cut-off on the month-end review.
          </div>
        </div>
      )}

      {/* The slide is the deliverable, so it gets the space.
          Side-by-side kicks in only above 1800px, where the preview still
          lands around 1000px+. Below that the two columns would each be
          starved, and stacking is genuinely better: the preview takes the
          full content width, so a 1600px screen renders the slide at its
          native size rather than at ~60%. */}
      <div ref={rowRef} className="flex flex-col min-[1400px]:flex-row gap-5 items-start">
        {/* ── Form ── */}
        <div
          className="w-full min-w-0 min-[1400px]:shrink-0"
          style={isWide ? { width: formW } : undefined}
        >
          <div className="bg-white rounded-xl border border-slate-200 p-1 flex gap-1 mb-4 flex-wrap">
            {TABS.map(t => (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors ${
                        tab === t.key ? 'bg-[#005EB8] text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                {t.label}
                {needs[t.key] && (
                  <span className={`w-1.5 h-1.5 rounded-full ${tab === t.key ? 'bg-amber-300' : 'bg-amber-400'}`}
                        title="Needs attention" />
                )}
              </button>
            ))}
          </div>

          {tab === 'status' && (
            <Card
              title="Status"
              info="Scope, Schedule, Cost and Benefit are derived from the risk log, dates, costs and hours ledger. Overrides require a reason, which is recorded on the report."

              action={
                suggested !== report.overall ? (
                  <button onClick={() => patch({ overall: suggested })}
                          className="text-xs text-danone-cyan font-semibold hover:text-danone-blue">
                    Suggested: {RAG_LABEL[suggested]}
                  </button>
                ) : undefined
              }
            >
              <Field label="Overall status" className="mb-2">
                <div className="flex items-center gap-2">
                  {RAGS.map(r => (
                    <button key={r} type="button" onClick={() => patch({ overall: r })}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                              report.overall === r ? `${RAG_SOLID[r]} text-white border-transparent` : RAG_PILL[r]}`}>
                      {RAG_LABEL[r]}
                    </button>
                  ))}
                  {report.overallPrevious && (
                    <span className="text-xs text-slate-400 ml-1">
                      Last month: {RAG_LABEL[report.overallPrevious]}
                    </span>
                  )}
                </div>
              </Field>
              <div className="mt-3">
                {(['scope', 'schedule', 'cost', 'benefit'] as const).map(k => (
                  <DimensionRow
                    key={k}
                    label={k}
                    value={report[k]}
                    onChange={a => patch({ [k]: a } as Partial<Report>)}
                  />
                ))}
              </div>
            </Card>
          )}

          {tab === 'summary' && (
            <Card title="Summary">
              <Field label={`Why ${RAG_LABEL[report.overall]}`} className="mb-4"
                     info="One line, shown above the narrative on the slide.">
                <RichTextInput value={report.whyLine} onChange={v => patch({ whyLine: v })}
                               placeholder="e.g. No agreed funding; majority of delivery deferred to 2027"
                               className={inputClass} />
              </Field>
              <Field label="Narrative"
                     info="New lines separate paragraphs. Formatting: ⌘B bold, ⌘I italic, ⌘U underline, ⌘⇧H highlight.">
                <RichTextInput value={report.narrative} onChange={v => patch({ narrative: v })}
                               multiline rows={9} toolbar="always"
                               className={`${inputClass} resize-y`}
                               placeholder="Progress, decisions and issues this month." />
              </Field>
              {summaryClipped && (
                <div className="mt-2 text-xs text-[#B8640B] font-semibold flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Too long for the slide at its smallest text size. Trim to avoid clipping.
                </div>
              )}
            </Card>
          )}

          {tab === 'content' && (
            <div className="space-y-4">
              <Card title="Highlights" info="What went well this month. Starts empty each month.">
                <BulletEditor items={report.highlights} onChange={b => patch({ highlights: b })}
                              placeholder="Add highlight" accent="bg-[#00843D]" addDisabled={noRoomForRow} />
              </Card>

              <Card title="Lowlights"
                    info="The month's setbacks, in your own words. Risks have their own block below.">
                <BulletEditor items={report.lowlights} onChange={b => patch({ lowlights: b })}
                              placeholder="Add lowlight" accent="bg-[#EF3340]" addDisabled={noRoomForRow} />
              </Card>

              <Card
                title="Open Risks"
                info="Pulled from the project's risk register. Rewording here changes only what this slide says; the register is untouched."
                action={
                  <label className={`flex items-center gap-1.5 text-xs font-medium select-none ${
                    noRoomForRisks ? 'text-slate-300 cursor-not-allowed' : 'text-slate-500 cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={!report.hideRisks}
                      disabled={noRoomForRisks}
                      onChange={e => patch({ hideRisks: !e.target.checked })}
                      className="w-3.5 h-3.5 rounded disabled:cursor-not-allowed"
                      style={{ accentColor: '#005EB8' }}
                    />
                    Show on slide
                  </label>
                }
              >
                {report.hideRisks ? (
                  <div className="text-sm text-slate-400 italic">
                    {noRoomForRisks ? (
                      <span className="not-italic flex items-start gap-1.5 text-[#B8640B] font-medium">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        No space remaining on the slide. Shorten or remove highlights, lowlights or
                        asks before the Open Risks block can come back.
                      </span>
                    ) : (
                      "The Open Risks block is left off this month's slide."
                    )}
                  </div>
                ) : (previewReport.snapshot.risks ?? []).length === 0 ? (
                  <div className="text-sm text-slate-400 italic">
                    No open risks on this project. Log them on the project page.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(previewReport.snapshot.risks ?? []).map(r => {
                      const reworded = !!r.sourceTitle
                      return (
                        <div key={r.id} className="flex items-start gap-2">
                          <span className={`w-2 h-2 rounded-full shrink-0 mt-2.5 ${RAG_SOLID[r.rag]}`}
                                title={`${RAG_LABEL[r.rag]} risk`} />
                          <div className="flex-1 min-w-0">
                            <input
                              value={r.title}
                              onChange={e => patch({
                                riskLabels: { ...(report.riskLabels ?? {}), [r.id]: e.target.value },
                              })}
                              className={smallInput}
                            />
                            {reworded && (
                              <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-400">
                                <span className="truncate">Register: {r.sourceTitle}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const next = { ...(report.riskLabels ?? {}) }
                                    delete next[r.id]
                                    patch({ riskLabels: next })
                                  }}
                                  className="text-danone-cyan font-semibold hover:text-danone-blue shrink-0"
                                >
                                  Reset
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                    {(listsSpace?.hidden.risks ?? 0) > 0 && (
                      <p className="text-[11px] text-[#B8640B] font-medium">
                        Only the {(previewReport.snapshot.risks?.length ?? 0) - listsSpace!.hidden.risks} worst
                        fit on the slide; the rest show as a count.
                      </p>
                    )}
                  </div>
                )}
              </Card>

              <Card title="Where we need your help" info="Stakeholder asks. Shown as the orange callout on the slide.">
                <BulletEditor items={report.supportNeeded} onChange={b => patch({ supportNeeded: b })}
                              placeholder="Add an ask" accent="bg-[#F88806]" addDisabled={noRoomForAsk} />
              </Card>
            </div>
          )}

          {tab === 'milestones' && (
            <Card title="Milestones"
                  info="Stored on the project. The report keeps a copy of them as at the date you save.">
              <MilestoneEditor items={milestones} onChange={m => { setMilestones(m); saver.edited() }} />
            </Card>
          )}

          {tab === 'details' && (
            <Card title="Governance & budget">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Stage">
                  <Select value={stage ?? ''} onChange={v => { setStage((v || undefined) as ProjectStage); saver.edited() }}
                          options={PROJECT_STAGES.map(s => [s.key, s.label] as const)} placeholder="Not set" />
                </Field>
                <Field label="Start date" info="Programme start. Shown as the left side of Window on the slide.">
                  <DateInput value={startDate} onChange={v => { setStartDate(v); saver.edited() }} />
                </Field>
                <Field label="End date"
                       info="Programme end, which is not the go-live date. Falls back to the due date when unset.">
                  <DateInput value={endDate} onChange={v => { setEndDate(v); saver.edited() }} />
                </Field>
                {/* Free text allowed: the VP and PM here are display labels on
                    a slide, not access control, and the person is routinely
                    someone who has never signed into TrackIT. */}
                <Field label="Functional Project Lead"
                       info="The business-side lead, distinct from the sponsoring VP and the delivery manager.">
                  <UserPicker value={lead} onChange={u => { setLead(u); saver.edited() }}
                              placeholder="Select or type a name" allowFreeText freeTextHint="Use this name" />
                </Field>
                <Field label="Functional VP">
                  <UserPicker value={vp} onChange={u => { setVp(u); saver.edited() }}
                              placeholder="Select or type a name" allowFreeText freeTextHint="Use this name" />
                </Field>
                <Field label="Project manager">
                  <UserPicker value={pm} onChange={u => { setPm(u); saver.edited() }}
                              placeholder="Select or type a name" allowFreeText freeTextHint="Use this name" />
                </Field>
                <Field label={`${budgetYear} CAPEX (€)`}>
                  <input type="number" value={capex} onChange={e => { setCapex(e.target.value); saver.edited() }}
                         className={inputClass} placeholder="0" />
                </Field>
                <Field label={`${budgetYear} OPEX (€)`}>
                  <input type="number" value={opex} onChange={e => { setOpex(e.target.value); saver.edited() }}
                         className={inputClass} placeholder="0" />
                </Field>
                <Field label={`${budgetYear} CAPEX committed (€)`}
                       info="Optional. Draws a spend bar against the allocation.">
                  <input type="number" value={capexCommitted}
                         onChange={e => { setCapexCommitted(e.target.value); saver.edited() }}
                         className={inputClass} placeholder="Not tracked" />
                </Field>
                <Field label="Report date" info="The governance meeting date, not the reporting period. Marks today on the timeline.">
                  <DateInput value={report.reportDate ?? ''} onChange={v => patch({ reportDate: v })} />
                </Field>
                <Field label="Date detail on slide"
                       info="Applies to Window and Go-Live.">
                  <Select
                    value={report.datePrecision ?? 'day'}
                    onChange={v => patch({ datePrecision: v as DatePrecision })}
                    options={[['day', 'Full date (22 Jul 2026)'], ['month', 'Month only (Jul 2026)'], ['quarter', 'Quarter only (Q3 2026)'], ['tbc', 'Not yet published (TBC)']]}
                  />
                </Field>
              </div>
            </Card>
          )}
        </div>

        {/* ── Live preview ── */}
        {/* Drag handle. Hit area is wider than the visible line so it is easy
            to grab; double-click restores the default split. */}
        {isWide && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the form and preview"
            aria-valuenow={Math.round(formW)}
            tabIndex={0}
            onPointerDown={startDrag}
            onPointerMove={onDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onDoubleClick={resetSplit}
            onKeyDown={nudge}
            title="Drag to resize · double-click to reset"
            className="self-stretch w-3 -mx-1.5 shrink-0 cursor-col-resize flex items-center justify-center group touch-none focus:outline-none"
          >
            <div className="w-[3px] h-16 rounded-full bg-slate-200 group-hover:bg-[#005EB8]/50 group-focus:bg-[#005EB8] transition-colors" />
          </div>
        )}

        <div ref={previewRef} className="w-full min-[1400px]:flex-1 min-[1400px]:min-w-0 min-[1400px]:sticky min-[1400px]:top-0">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Slide preview
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => download(`${project?.name ?? 'Status report'} ${monthLabel(month)}`)}
                      disabled={exporting}
                      className="text-xs text-danone-cyan font-semibold hover:text-danone-blue flex items-center gap-1 disabled:opacity-60">
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download PNG
              </button>
              <button onClick={() => setFullSize(true)}
                      className="text-xs text-danone-cyan font-semibold hover:text-danone-blue flex items-center gap-1"
                      title="Open full screen">
                <Maximize2 className="w-3.5 h-3.5" /> Full screen
              </button>
            </div>
          </div>
          <div className="rounded-lg overflow-hidden shadow-sm border border-slate-200 max-w-full"
               style={{ width: SLIDE_W * scale, height: SLIDE_H * scale }}>
            <div style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
              <div ref={slideRef} style={{ width: SLIDE_W, height: SLIDE_H }}>
                <ReportSlide report={previewReport} onSummaryOverflow={setSummaryClipped} onListsOverflow={setListsSpace} />
              </div>
            </div>
          </div>
          {exportError && (
            <p role="alert" className="text-[11px] text-[#B8242E] mt-2 font-semibold">{exportError}</p>
          )}
          {summaryClipped && (
            <p className="text-[11px] text-[#B8640B] mt-2 leading-snug font-semibold">
              Summary is clipping. Trim it in the Summary tab.
            </p>
          )}
          {hiddenSummary && (
            <p className="text-[11px] text-[#B8640B] mt-2 leading-snug font-semibold">
              Out of space on the slide: {hiddenSummary} not shown. Condense or remove items
              in Highlights &amp; Asks.
            </p>
          )}
        </div>
      </div>

      {fullSize && (
        <FullScreenSlide onClose={() => setFullSize(false)}>
          <ReportSlide report={previewReport} />
        </FullScreenSlide>
      )}
    </div>
  )
}
